import { useState } from 'preact/hooks';
import { UNITS, stockRow, parseQty, parseTaka } from '@khata/core';
import { DateInput, Field, MoneyInput, QtyInput, Select, TextInput } from '../components/index.js';
import { fromApi, isErr, money, quantity, useFields, useSave } from '../lib/form.js';
import { qty as fmtQty, taka } from '../lib/format.js';
import { t, toLatinDigits } from '../lib/i18n.js';
import { createItem, deleteItem, master, state, today, updateItem } from '../lib/khata.js';
import { closeSheet, openSheet, showToast } from '../lib/ui.js';
import { FormErrorLine, SaveButton, Sheet } from './Sheet.js';

const COMMON_ORIGINS = ['India', 'Japan', 'China', 'Bangladesh', 'Thailand'];

export function ItemSheet(props: { id?: string }) {
  const m = master.value;
  const s = state.value;
  const existing = props.id ? m.items.get(props.id) : undefined;
  const row = existing ? stockRow(s, existing) : null;
  const cats = [...new Set([...m.items.values()].map((i) => i.category))].sort();
  const origins = [...new Set([...m.items.values()].map((i) => i.origin).filter(Boolean))].sort();
  const [f, set] = useFields({
    name: existing?.name ?? '',
    category: existing?.category ?? '',
    origin: existing?.origin ?? 'India',
    unit: existing?.unit ?? 'pcs',
    minQty: existing ? String(existing.minQty / 1000) : '5',
    sellPrice: existing ? String(existing.sellPrice / 100) : '',
    openQty: '',
    openCost: '',
    openDate: today.value,
  });
  const [error, busy, save] = useSave();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const onSave = () =>
    save(async () => {
      if (!f.name.trim()) return { message: t('item_needsName'), field: 'name' };
      const minQty = f.minQty.trim() === '' ? 0 : parseQty(toLatinDigits(f.minQty));
      if (minQty === null || minQty < 0) return { message: t('item_lowInvalid'), field: 'minQty' };
      const sellPrice = f.sellPrice.trim() === '' ? 0 : parseTaka(toLatinDigits(f.sellPrice));
      if (sellPrice === null || sellPrice < 0)
        return { message: t('item_sellInvalid'), field: 'sellPrice' };
      const input = {
        name: f.name,
        category: f.category,
        origin: f.origin,
        unit: f.unit,
        minQty,
        sellPrice,
      };
      if (existing) {
        const r = await updateItem(existing.id, { ...input, active: true });
        return r.ok ? t('item_saved', { name: r.value.name }) : fromApi(r.error);
      }
      let opening: { date: string; qty: number; unitCost: number } | undefined;
      if (f.openQty.trim() !== '' && f.openQty.trim() !== '0') {
        const q = quantity(f.openQty, 'openQty');
        if (isErr(q)) return q;
        const c = money(f.openCost, 'openCost');
        if (isErr(c)) return { message: t('item_buyRequired'), field: 'openCost' };
        opening = { date: f.openDate, qty: q, unitCost: c };
      }
      const r = await createItem(input, opening);
      return r.ok ? t('item_added', { name: r.value.item.name }) : fromApi(r.error);
    });

  const onDelete = () =>
    save(async () => {
      if (!existing) return null;
      const r = await deleteItem(existing.id);
      if (!r.ok) return fromApi(r.error);
      return r.value.result === 'deleted'
        ? t('item_deleted')
        : t('item_archived', { name: existing.name });
    });

  return (
    <Sheet
      title={existing ? t('sheet_editItem') : t('sheet_newItem')}
      footer={<SaveButton busy={busy} onClick={onSave} />}
    >
      <Field label={t('item_name')}>
        <TextInput
          value={f.name}
          onInput={(v) => set('name', v)}
          placeholder={t('item_namePlaceholder')}
          invalid={error?.field === 'name'}
          autoFocus={!existing}
        />
      </Field>
      <div class="grid-2">
        <Field label={t('item_category')}>
          <TextInput
            value={f.category}
            onInput={(v) => set('category', v)}
            list="cats"
            placeholder={t('item_categoryDefault')}
          />
          <datalist id="cats">
            {cats.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
        <Field label={t('item_origin')}>
          <TextInput value={f.origin} onInput={(v) => set('origin', v)} list="origins" />
          <datalist id="origins">
            {[...COMMON_ORIGINS, ...origins.filter((o) => !COMMON_ORIGINS.includes(o))].map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
      </div>
      <div class="grid-3">
        <Field label={t('item_unit')}>
          <Select value={f.unit} onChange={(v) => set('unit', v)}>
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('item_lowAlert')}>
          <QtyInput
            value={f.minQty}
            onInput={(v) => set('minQty', v)}
            invalid={error?.field === 'minQty'}
          />
        </Field>
        <Field label={t('item_sellPrice')}>
          <MoneyInput
            value={f.sellPrice}
            onInput={(v) => set('sellPrice', v)}
            invalid={error?.field === 'sellPrice'}
          />
        </Field>
      </div>
      {existing && row ? (
        <>
          <div class="total-line">
            <span class="muted">{t('item_inStock')}</span>
            <strong>
              {fmtQty(row.qty)} {existing.unit}
              <span class="muted small">
                {' '}
                · {t('item_avgCost')} {taka(row.avgCost)}
              </span>
              {row.defQty > 0 && (
                <span class="muted small">
                  {' '}
                  · {fmtQty(row.defQty)} {t('stock_defective')}
                </span>
              )}
            </strong>
          </div>
          <div class="actions" style={{ marginBottom: 0 }}>
            <button
              type="button"
              class="btn btn-secondary"
              onClick={() => openSheet({ kind: 'adjust', itemId: existing.id })}
            >
              {t('item_adjust')}
            </button>
            {!confirmDelete ? (
              <button
                type="button"
                class="btn btn-ghost btn-danger"
                onClick={() => setConfirmDelete(true)}
              >
                {t('item_delete')}
              </button>
            ) : (
              <button
                type="button"
                class="btn btn-secondary btn-danger"
                onClick={onDelete}
                disabled={busy}
              >
                {t('confirmDelete')}
              </button>
            )}
          </div>
          {confirmDelete && <div class="help">{t('item_deleteHelp')}</div>}
        </>
      ) : (
        <>
          <h6 class="section" style={{ marginTop: 8 }}>
            {t('item_openingHead')}
          </h6>
          <div class="grid-3">
            <Field label={t('quantity')}>
              <QtyInput
                value={f.openQty}
                onInput={(v) => set('openQty', v)}
                invalid={error?.field === 'openQty'}
              />
            </Field>
            <Field label={t('item_buyPrice')}>
              <MoneyInput
                value={f.openCost}
                onInput={(v) => set('openCost', v)}
                invalid={error?.field === 'openCost'}
              />
            </Field>
            <Field label={t('item_asOf')}>
              <DateInput value={f.openDate} onInput={(v) => set('openDate', v)} />
            </Field>
          </div>
        </>
      )}
      <FormErrorLine error={error} />
      {existing && !existing.active && (
        <button
          type="button"
          class="btn btn-secondary"
          onClick={() =>
            void updateItem(existing.id, { active: true }).then((r) => {
              if (r.ok) {
                closeSheet();
                showToast(t('item_restored'));
              }
            })
          }
        >
          {t('item_restore')}
        </button>
      )}
    </Sheet>
  );
}
